import React from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, ReferenceLine } from 'recharts';
import { TrendingUp, AlertCircle } from 'lucide-react';
import { formatNumber } from '../../../../../utils/formatting';

/**
 * Компонент для отображения графика исторической инфляции
 */
const HistoricalRatesChart = ({ historicalData, selectedYears }) => {
  if (!historicalData || !historicalData.rates || historicalData.rates.length === 0) {
    return null;
  }

  // Фильтруем только необходимый диапазон данных
  const filteredData = historicalData.rates.filter(
    item => item.year >= Math.min(selectedYears.start, selectedYears.end - 15) && 
            item.year <= Math.max(selectedYears.end, selectedYears.start + 15)
  );

  // Выделяем выбранный период
  const selectedPeriodStart = Math.min(selectedYears.start, selectedYears.end);
  const selectedPeriodEnd = Math.max(selectedYears.start, selectedYears.end);

  // Вычисляем среднюю инфляцию за 5 и 10 лет
  const averageFiveYear = historicalData.aggregated.averageFiveYear;
  const averageTenYear = historicalData.aggregated.averageTenYear;

  // Кастомный тултип
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const isSelected = label >= selectedPeriodStart && label <= selectedPeriodEnd;
      return (
        <div className={`bg-white dark:bg-gray-800 p-3 border ${isSelected ? 'border-blue-300 dark:border-blue-700' : 'border-gray-200 dark:border-gray-700'} rounded shadow-lg`}>
          <p className="text-sm font-medium text-gray-900 dark:text-white mb-1">
            {label} год {isSelected && <span className="text-blue-500">(выбранный период)</span>}
          </p>
          <p className="text-sm text-gray-700 dark:text-gray-300">
            Инфляция: <span className="font-medium">{formatNumber(payload[0].value, 1)}%</span>
          </p>
          {payload[1] && (
            <p className="text-sm text-gray-700 dark:text-gray-300">
              Ключевая ставка: <span className="font-medium">{formatNumber(payload[1].value, 1)}%</span>
            </p>
          )}
        </div>
      );
    }
    return null;
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm p-5 mt-8">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white flex items-center">
          <TrendingUp className="w-5 h-5 mr-2 text-blue-500" />
          Историческая инфляция в России
        </h3>
      </div>

      {/* Информационная подсказка */}
      <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-100 dark:border-blue-800 rounded p-3 mb-4 flex items-start">
        <AlertCircle className="text-blue-500 dark:text-blue-400 w-5 h-5 mr-2 flex-shrink-0 mt-0.5" />
        <div className="text-sm text-blue-700 dark:text-blue-300">
          <p>График показывает официальные данные по инфляции в России. Выбранный период расчета выделен темным цветом.</p>
        </div>
      </div>

      {/* График */}
      <div className="w-full h-72 mt-4">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            data={filteredData}
            margin={{ top: 5, right: 20, left: 10, bottom: 5 }}
          >
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis 
              dataKey="year" 
              domain={['dataMin', 'dataMax']}
              type="number"
              tickFormatter={(year) => year.toString()}
              allowDecimals={false}
            />
            <YAxis 
              tickFormatter={(value) => `${value}%`}
              domain={[0, 'auto']}
            />
            <Tooltip content={<CustomTooltip />} />
            <Legend />
            <Bar 
              dataKey="inflationRate" 
              name="Инфляция, %" 
              fill={(data) => {
                return data.year >= selectedPeriodStart && data.year <= selectedPeriodEnd 
                  ? '#3b82f6' // Синий для выбранного периода
                  : '#93c5fd' // Светло-синий для остальных
              }}
              radius={[4, 4, 0, 0]}
            />
            <Bar 
              dataKey="keyRate" 
              name="Ключевая ставка, %" 
              fill="#f59e0b"
              radius={[4, 4, 0, 0]}
              opacity={0.7}
            />
            <ReferenceLine y={averageFiveYear} stroke="#10b981" strokeDasharray="3 3" label={{ position: 'right', value: `Средняя за 5 лет (${formatNumber(averageFiveYear, 1)}%)`, fill: '#10b981', fontSize: 12 }} />
            <ReferenceLine y={averageTenYear} stroke="#6366f1" strokeDasharray="3 3" label={{ position: 'right', value: `Средняя за 10 лет (${formatNumber(averageTenYear, 1)}%)`, fill: '#6366f1', fontSize: 12 }} />
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Итоговое резюме */}
      <div className="mt-4 p-3 bg-gray-50 dark:bg-gray-700/30 rounded border border-gray-200 dark:border-gray-700">
        <p className="text-sm text-gray-600 dark:text-gray-400">
          Наибольшая годовая инфляция в России за последние 25 лет составила {formatNumber(historicalData.aggregated.maxRate, 1)}%, 
          наименьшая – {formatNumber(historicalData.aggregated.minRate, 1)}%. 
          В среднем за последние 5 лет инфляция составляла {formatNumber(averageFiveYear, 1)}%.
        </p>
      </div>
    </div>
  );
};

export default HistoricalRatesChart;